import { useMemo } from 'react';
import type { RequestToken } from '../types';
import {
  useFindManyAttributeLocalesQuery,
  useFindManyCredentialLocalesQuery,
  useFindManyIssuerLocalesQuery,
  AttributeLocaleFilteringField,
  CredentialLocaleFilteringField,
  IssuerLocaleFilteringField,
  FilteringConnector,
  FilteringType,
} from '../types/graphql';
import { useFetchAllPages } from './useFetchAllPages';
import { useIssuerLogos } from './useIssuerLogos';

// Maximum page size allowed by the server.
const PAGE_SIZE = 100;

/**
 * A single attribute field within a requested data group.
 */
export type RequestedField = {
  attributeUuid: string;
  name: string;
  description?: string;
};

/**
 * A group of requested data, grouped by credential.
 */
export type RequestedDataGroup = {
  credentialUuid: string;
  name: string;
  issuerName?: string;
  logo?: string;
  fields: RequestedField[];
};

/**
 * Extracts unique attribute UUIDs and grouping keys from the token.
 *
 * Both issuance and disclosure tokens contain `credentialUuid` per query,
 * so we always group by credential. Also builds a credentialUuid → issuerUuid
 * map for resolving issuer names and logos.
 */
function extractUuids(selectedToken: RequestToken | null) {
  // credentialUuid → Set<attributeUuid>
  const groupMap = new Map<string, Set<string>>();
  // credentialUuid → issuerUuid
  const credentialToIssuer = new Map<string, string>();

  if (!selectedToken) {
    return {
      groupMap,
      credentialToIssuer,
      attributeUuids: [] as string[],
      credentialUuids: [] as string[],
      issuerUuids: [] as string[],
    };
  }

  const body = selectedToken.payload.body;

  const queries =
    'groups' in body ? body.groups.flatMap((g) => g.queries) : body.queries;

  for (const query of queries) {
    const key = query.credentialUuid;
    if (!key) {
      throw new Error(
        `[useRequestedData] Query missing credentialUuid. Token body: ${JSON.stringify(body).slice(0, 500)}`,
      );
    }
    if (!groupMap.has(key)) {
      groupMap.set(key, new Set());
    }
    credentialToIssuer.set(key, query.issuerUuid);
    const entry = groupMap.get(key)!;
    for (const field of query.fields) {
      if (!field.attributeUuid) {
        throw new Error(
          `[useRequestedData] Field missing attributeUuid in credential ${key}`,
        );
      }
      entry.add(field.attributeUuid);
    }
  }

  if (groupMap.size === 0) {
    throw new Error(
      `[useRequestedData] Token contains no queries. Body keys: ${Object.keys(body).join(', ')}`,
    );
  }

  const attributeUuids: string[] = [];
  for (const uuids of groupMap.values()) {
    for (const uuid of uuids) attributeUuids.push(uuid);
  }

  return {
    groupMap,
    credentialToIssuer,
    attributeUuids,
    credentialUuids: Array.from(groupMap.keys()),
    issuerUuids: Array.from(new Set(credentialToIssuer.values())),
  };
}

/**
 * Extracts requested data from the selected token and fetches localized
 * credential, issuer, and attribute names via GraphQL.
 *
 * Both issuance and disclosure tokens group by credential. Each group
 * gets a credential name (from credential locale) and an issuer name +
 * logo (from issuer locale and issuer model).
 *
 * Always fetches attribute locales for field label + description.
 *
 * Throws on GraphQL errors or unexpected token structure to prevent
 * silent failures.
 */
export function useRequestedData(
  selectedToken: RequestToken | null,
  locale: string,
): { groups: RequestedDataGroup[]; loading: boolean } {
  const {
    groupMap,
    credentialToIssuer,
    attributeUuids,
    credentialUuids,
    issuerUuids,
  } = useMemo(() => extractUuids(selectedToken), [selectedToken]);

  const skip = attributeUuids.length === 0;

  // Fetch attribute locales (for field labels + descriptions). The total number
  // of attribute locales is (attributes × languages), which can exceed a single
  // page, so we fetch every page.
  const {
    data: attrData,
    loading: attrLoading,
    error: attrError,
  } = useFetchAllPages({
    useQuery: useFindManyAttributeLocalesQuery,
    connectionKey: 'findManyAttributeLocales',
    skip,
    variables: {
      input: {
        filtering: attributeUuids.map((uuid) => ({
          connector: FilteringConnector.Disjunction,
          field: AttributeLocaleFilteringField.AttributeUuid,
          value: uuid,
          type: FilteringType.Exact,
        })),
        pagination: { first: PAGE_SIZE },
      },
    },
  });

  // Fetch credential locales for group names (all pages).
  const {
    data: credentialData,
    loading: credentialLoading,
    error: credentialError,
  } = useFetchAllPages({
    useQuery: useFindManyCredentialLocalesQuery,
    connectionKey: 'findManyCredentialLocales',
    skip,
    variables: {
      input: {
        filtering: credentialUuids.map((uuid) => ({
          connector: FilteringConnector.Disjunction,
          field: CredentialLocaleFilteringField.CredentialUuid,
          value: uuid,
          type: FilteringType.Exact,
        })),
        pagination: { first: PAGE_SIZE },
      },
    },
  });

  // Fetch issuer locales for issuer name subtitle (all pages).
  const {
    data: issuerData,
    loading: issuerLoading,
    error: issuerError,
  } = useFetchAllPages({
    useQuery: useFindManyIssuerLocalesQuery,
    connectionKey: 'findManyIssuerLocales',
    skip: skip || issuerUuids.length === 0,
    variables: {
      input: {
        filtering: issuerUuids.map((uuid) => ({
          connector: FilteringConnector.Disjunction,
          field: IssuerLocaleFilteringField.IssuerUuid,
          value: uuid,
          type: FilteringType.Exact,
        })),
        pagination: { first: PAGE_SIZE },
      },
    },
  });

  // Fetch issuer logos from issuer model
  const { logos: issuerLogos, loading: logosLoading } =
    useIssuerLogos(issuerUuids);

  // Throw on GraphQL errors so they surface instead of silently returning []
  if (attrError) {
    throw new Error(
      `[useRequestedData] Failed to fetch attribute locales: ${attrError.message}`,
    );
  }
  if (credentialError) {
    throw new Error(
      `[useRequestedData] Failed to fetch credential locales: ${credentialError.message}`,
    );
  }
  if (issuerError) {
    throw new Error(
      `[useRequestedData] Failed to fetch issuer locales: ${issuerError.message}`,
    );
  }

  const groups = useMemo(() => {
    if (skip || !attrData || !credentialData) return [];

    const attrEdges = attrData.findManyAttributeLocales.edges.filter(Boolean);

    // Build attribute UUID → { label, description } map
    const attrInfoMap: Record<string, { name: string; description?: string }> =
      {};
    for (const uuid of attributeUuids) {
      const match =
        attrEdges.find(
          (e) => e.node.attribute.uuid === uuid && e.node.locale === locale,
        ) ??
        attrEdges.find(
          (e) => e.node.attribute.uuid === uuid && e.node.locale === 'en',
        );
      if (match?.node.label) {
        attrInfoMap[uuid] = {
          name: match.node.label,
          description: match.node.description ?? undefined,
        };
      }
    }

    // Build credential UUID → localized name map + logo map
    const credentialNameMap: Record<string, string> = {};
    const credentialLogoMap: Record<string, string> = {};
    const credEdges =
      credentialData.findManyCredentialLocales.edges.filter(Boolean);
    for (const uuid of credentialUuids) {
      const match =
        credEdges.find(
          (e) => e.node.credential.uuid === uuid && e.node.locale === locale,
        ) ??
        credEdges.find(
          (e) => e.node.credential.uuid === uuid && e.node.locale === 'en',
        );
      if (match?.node.name) credentialNameMap[uuid] = match.node.name;
      if (match?.node.logo) credentialLogoMap[uuid] = match.node.logo;
    }

    // Build issuer UUID → localized name map
    const issuerNameMap: Record<string, string> = {};
    if (issuerData) {
      const edges = issuerData.findManyIssuerLocales.edges.filter(Boolean);
      for (const uuid of issuerUuids) {
        const match =
          edges.find(
            (e) => e.node.issuer.uuid === uuid && e.node.locale === locale,
          ) ??
          edges.find(
            (e) => e.node.issuer.uuid === uuid && e.node.locale === 'en',
          );
        if (match?.node.name) issuerNameMap[uuid] = match.node.name;
      }
    }

    const result: RequestedDataGroup[] = [];
    for (const [credentialUuid, attrUuids] of groupMap) {
      const issuerUuid = credentialToIssuer.get(credentialUuid);
      // Prefer credential locale logo, fall back to issuer base64Logo
      const logo =
        credentialLogoMap[credentialUuid] ??
        (issuerUuid ? issuerLogos[issuerUuid] : undefined);
      result.push({
        credentialUuid,
        name: credentialNameMap[credentialUuid] ?? credentialUuid,
        issuerName: issuerUuid ? issuerNameMap[issuerUuid] : undefined,
        logo,
        fields: Array.from(attrUuids).map((attrUuid) => ({
          attributeUuid: attrUuid,
          name: attrInfoMap[attrUuid]?.name ?? attrUuid,
          description: attrInfoMap[attrUuid]?.description,
        })),
      });
    }

    return result;
  }, [
    skip,
    attrData,
    credentialData,
    issuerData,
    issuerLogos,
    groupMap,
    credentialToIssuer,
    attributeUuids,
    credentialUuids,
    issuerUuids,
    locale,
  ]);

  const loading =
    attrLoading || credentialLoading || issuerLoading || logosLoading;

  return { groups, loading };
}
